import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { PhysicsData, ChemistryData, LabData } from "../models/coreModels";

export const getLocations = async (locationIds?: string) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "LocationsID", value: locationIds || null },
  ];
  return await apiService.callV2("[BYI].[GetBilletsLocations]", parameters);
};

// Targeted location refresh for modal - matches legacy RefreshMoveScreenData pattern
export const getLocationsByIds = async (
  sourceLocationId: number,
  destinationLocationId: number,
) => {
  const locationIds = `${sourceLocationId},${destinationLocationId}`;
  return await getLocations(locationIds);
};

// Move Billets API call - matching legacy BYI.MoveBillets procedure
export const moveBillets = async (
  locationContentIDOrigin: number,
  locationIDOrigin: number,
  positionOrigin: number,
  locationContentIDDestiny: number | null, // Can be null for empty destinations
  locationIDDestiny: number,
  positionDestiny: number | null, // Can be null to let SQL determine position based on AddFillingType
  billetsCount: number,
  craneID: number,
  checkValidation: boolean = true,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "LocationContentIDOrigin", value: locationContentIDOrigin },
    { name: "LocationIDOrigin", value: locationIDOrigin },
    { name: "PositionOrigin", value: positionOrigin },
    { name: "LocationContentIDDestiny", value: locationContentIDDestiny },
    { name: "LocationIDDestiny", value: locationIDDestiny },
    { name: "PositionDestiny", value: positionDestiny },
    { name: "BilletsCount", value: billetsCount },
    { name: "CraneID", value: craneID },
    { name: "CheckValidation", value: checkValidation },
  ];
  return await apiService.callV2("[BYI].[MoveBillets]", parameters);
};

// Add Billets API call - matching legacy BYI.AddBillet procedure
export const addBillet = async (
  locationContentID: number | null,
  locationID: number,
  position: number,
  billetsCount: number,
  billetsGroupID: number,
  craneID: number,
  checkValidation: boolean = true,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "LocationContentID", value: locationContentID },
    { name: "LocationID", value: locationID },
    { name: "Position", value: position },
    { name: "BilletsCount", value: billetsCount },
    { name: "BilletsGroupID", value: billetsGroupID },
    { name: "CraneID", value: craneID },
    { name: "CheckValidation", value: checkValidation },
  ];
  return await apiService.callV2("[BYI].[AddBillet]", parameters);
};

// Create Billets Group API call - business logic moved to repository layer
export const createBilletsGroup = async (
  heatNumber: string,
  dimensionsString: string, // Format: "width,height" - will be parsed here
  quality: string,
  length: number,
  isForSale: boolean,
  gradeID: number,
  checkExistence: boolean = true,
) => {
  const apiService = new MESApiService();

  // Business logic moved from proxy to repository layer

  // 1. Parse dimensions string "width,height" into separate values
  let width: number | null = null;
  let height: number | null = null;

  if (dimensionsString && dimensionsString.includes(",")) {
    const parts = dimensionsString.split(",");
    if (parts.length === 2) {
      height = parseFloat(parts[0].trim()); // to send height width
      width = parseFloat(parts[1].trim());
    }
  }

  // 2. Data transformations to match production procedure requirements
  const processedHeatNumber = heatNumber
    ? heatNumber.substring(0, 6).toUpperCase()
    : ""; // Truncate to 6 chars
  const processedLength = Math.round(length); // Convert to INT (rounded)
  const processedQuality = quality ? quality.substring(0, 1) : ""; // Truncate to 1 char
  const casterMoldID = null; // Always null as per old system

  const parameters: spParameter[] = [
    { name: "HeatNumber", value: processedHeatNumber },
    { name: "CasterMoldID", value: casterMoldID },
    { name: "Width", value: width },
    { name: "Height", value: height },
    { name: "Length", value: processedLength },
    { name: "BilletsQualityStatusCode", value: processedQuality },
    { name: "isForSale", value: isForSale },
    { name: "GradeID", value: gradeID },
    { name: "CheckExistence", value: checkExistence },
  ];
  return await apiService.callV2("[BYI].[CreateBilletsGroup]", parameters);
};

// Clear Location API call - matching legacy BYI.ClearLocation procedure
export const clearLocation = async (
  locationID: number,
  craneID: number,
  checkValidation: boolean = true,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "LocationID", value: locationID },
    { name: "CraneID", value: craneID },
    // { name: "CheckValidation", value: checkValidation }, // TODO: maybe not needed. We resolve this using Profiles
  ];
  return await apiService.callV2("[BYI].[ClearLocation]", parameters);
};

// Set Billets Count API call - matching legacy BYI.SetBilletsCount procedure
export const setBilletsCount = async (
  locationContentID: number,
  locationID: number | null = null,
  position: number | null = null,
  billetsCount: number,
  checkValidation: boolean = true,
  checkProcParams: boolean = true,
  checkExistence: boolean = true,
  checkPositionExistence: boolean = true,
  logTransaction: boolean = true,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "LocationContentID", value: locationContentID },
    { name: "LocationID", value: locationID },
    { name: "Position", value: position },
    { name: "BilletsCount", value: billetsCount },
    { name: "CheckValidation", value: checkValidation },
    { name: "CheckProcParams", value: checkProcParams },
    { name: "CheckExistence", value: checkExistence },
    { name: "CheckPositionExistence", value: checkPositionExistence },
    { name: "LogTransaction", value: logTransaction },
  ];
  return await apiService.callV2("[BYI].[SetBilletsCount]", parameters);
};

//Dropdown data repositories (matching legacy lookup procedures)

// Get available grades - BYI.GetGradesFDDP (calls legacy dbo.GetGradesFDDP)
export const getGrades = async () => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  return await apiService.callV2("[BYI].[GetGradesFDDP]", parameters);
};

// Get billet quality statuses - BYI.GetBilletQualityStatusFDDP
export const getBilletQualityStatuses = async () => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  return await apiService.callV2(
    "[BYI].[GetBilletQualityStatusFDDP]",
    parameters,
  );
};

// Get available sections (dimensions) - BYI.GetSectionsFDDP (calls legacy MES.GetSectionsFDDP)
export const getSections = async () => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  return await apiService.callV2("[BYI].[GetSectionsFDDP]", parameters);
};

// Get available lengths - BYI.GetLengthsFDDP (calls legacy MES.GetLengthsFDDP)
export const getLengths = async () => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  return await apiService.callV2("[BYI].[GetLengthsFDDP]", parameters);
};

// Get yard locations for dropdown - BYI.GetLocationsDDP
export const getLocationsDropdown = async () => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  return await apiService.callV2("[BYI].[GetLocationsDDP]", parameters);
};

// Get yard heats for dropdown - BYI.GetYardHeatsDDP
export const getYardHeats = async () => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  return await apiService.callV2("[BYI].[GetYardHeatsDDP]", parameters);
};

// Get available cranes for dropdown - BYI.GetCranesFDDP
export const getCranes = async () => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  return await apiService.callV2("[BYI].[GetCranesFDDP]", parameters);
};

// Get Billet Yard Operators - now implemented via proxy procedure with adapter
export const getBilletYardOperators = async () => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [];
  const response = await apiService.callV2(
    "[BYI].[GetOperatorsFDDP]",
    parameters,
  );

  if (response?.ok && response.data?.tables?.[0]?.rows) {
    const rows = response.data.tables[0].rows;

    if (rows.length > 0 && "Title" in rows[0] && "Value" in rows[0]) {
      response.data.tables[0].rows = rows.map((row: any) => ({
        OperatorId: row.Value,
        OperatorName: row.Title,
      }));
    }
  }

  return response;
};

// Crane login/logoff operations
export const loginOperatorInCrane = async (
  craneID: number,
  operatorID: number,
  checkValidation: boolean = true,
  checkOperatorLogged: boolean = true,
  checkProcParams: boolean = true,
  checkExistence: boolean = true,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "CraneID", value: craneID },
    { name: "OperatorID", value: operatorID },
    { name: "CheckValidation", value: checkValidation },
    { name: "CheckOperatorLogged", value: checkOperatorLogged },
    { name: "CheckProcParams", value: checkProcParams },
    { name: "CheckExistence", value: checkExistence },
  ];
  return await apiService.callV2("[BYI].[LoginOperatorInCrane]", parameters);
};

export const logoffOperatorOfCrane = async (
  craneID: number,
  checkProcParams: boolean = true,
  checkExistence: boolean = true,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "CraneID", value: craneID },
    { name: "CheckProcParams", value: checkProcParams },
    { name: "CheckExistence", value: checkExistence },
  ];
  return await apiService.callV2("[BYI].[LogoffOperatorOfCrane]", parameters);
};

// Get current crane assigned to operator (for session recovery after page refresh)
export const getCraneOfOperator = async (operatorID: number) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "OperatorID", value: operatorID }];
  return await apiService.callV2("[BYI].[GetOperatorCrane]", parameters);
};

// Upsert the heat-level comment (fans out to all the heat's billet groups,
// except UN placeholder heats where it only touches the given group)
export const upsertHeatComment = async (
  heatName: string,
  comments: string,
  billetsGroupId: number,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "HeatName", value: heatName },
    { name: "Comments", value: comments ?? "" },
    { name: "BilletsGroupID", value: billetsGroupId },
  ];
  return await apiService.callV2("[BYI].[UpsertHeatComment]", parameters);
};

// Get lab data (physics and chemistry) for a heat
export const getLabData = async (heat: string): Promise<LabData> => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "HeatName", value: heat }];
  const response = await apiService.callV2("[EXT].[GetLabData]", parameters);
  return {
    physics: get(response, "data.tables[0].rows", []) as PhysicsData[],
    chemistry: get(response, "data.tables[1].rows", []) as ChemistryData[],
  };
};
